// BioForge 2.2 — workspace shell: 3-panel resizable, command palette, insights slot.
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { Command } from "cmdk";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import BreadcrumbNav from "@/breadcrumb";
import {
  LayoutDashboard, FolderKanban, Sparkles, BookOpen, FileText, Search,
  Lightbulb, Wrench, Activity, ShieldCheck, FlaskConical, Building2, AlertOctagon,
  TrendingUp, ScrollText, Network, ListTree, ChevronLeft, ChevronRight, PanelLeftClose,
  PanelRightClose, Command as CmdIcon, Rocket, ArrowRight, Atom, BadgeDollarSign, LayoutGrid,
  Factory, Compass,
} from "lucide-react";
import { useRunsList, DEMO_SCENARIOS, apiPost } from "@/lib";
import { PersonaPill } from "@/persona";
import { toast } from "sonner";

const FEATURED = "fc526bce-d199-433b-8475-8ba28829527f";

// ---------- Layout context: any view can push content to the right panel ----------
const LayoutCtx = createContext({
  insights: null, setInsights: () => {},
  cmdOpen: false, setCmdOpen: () => {},
  leftCollapsed: false, setLeftCollapsed: () => {},
  rightCollapsed: false, setRightCollapsed: () => {},
});

export function useLayout() { return useContext(LayoutCtx); }
export function useInsights(node, deps = []) {
  const { setInsights } = useLayout();
  useEffect(() => {
    setInsights(node);
    return () => setInsights(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export function LayoutProvider({ children }) {
  const [insights, setInsights] = useState(null);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault(); setCmdOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const value = useMemo(
    () => ({ insights, setInsights, cmdOpen, setCmdOpen, leftCollapsed, setLeftCollapsed, rightCollapsed, setRightCollapsed }),
    [insights, cmdOpen, leftCollapsed, rightCollapsed]
  );
  return <LayoutCtx.Provider value={value}>{children}</LayoutCtx.Provider>;
}

// ---------- The shell ----------
export function Shell() {
  const location = useLocation();
  const { leftCollapsed, rightCollapsed, setLeftCollapsed, setRightCollapsed } = useLayout();
  const isPitch = location.pathname.endsWith("/pitch");
  const m = location.pathname.match(/^\/projects\/([^/]+)/);
  const ventureId = m && m[1] !== "new" ? m[1] : null;

  // Pitch tab: hide both panels entirely.
  if (isPitch) {
    return (
      <div className="app-bg min-h-screen">
        <CommandPalette />
        <Outlet />
      </div>
    );
  }

  return (
    <div className="app-bg min-h-screen overflow-hidden" style={{ height: "100vh" }}>
      <CommandPalette />
      <PanelGroup direction="horizontal" className="h-screen">
        <Panel
          defaultSize={16}
          minSize={leftCollapsed ? 4 : 14}
          maxSize={leftCollapsed ? 4 : 22}
          collapsible
          collapsedSize={4}
          onCollapse={() => setLeftCollapsed(true)}
          onExpand={() => setLeftCollapsed(false)}
          className="border-r border-white/[0.06]"
        >
          <LeftSidebar collapsed={leftCollapsed} onToggle={() => setLeftCollapsed((v) => !v)} ventureId={ventureId} />
        </Panel>
        <PanelResizeHandle />
        <Panel minSize={40} className="overflow-hidden">
          <main className="h-full overflow-y-auto scrollbar-thin">
            <BreadcrumbVisibility />
            <Outlet />
          </main>
        </Panel>
        <PanelResizeHandle />
        <Panel
          defaultSize={22}
          minSize={rightCollapsed ? 3 : 16}
          maxSize={rightCollapsed ? 3 : 30}
          collapsible
          collapsedSize={3}
          onCollapse={() => setRightCollapsed(true)}
          onExpand={() => setRightCollapsed(false)}
          className="border-l border-white/[0.06]"
        >
          <RightInsights collapsed={rightCollapsed} onToggle={() => setRightCollapsed((v) => !v)} />
        </Panel>
      </PanelGroup>
    </div>
  );
}

// ---------- Left Sidebar ----------
const WORKSPACE_LINKS = [
  { to: "/", label: "Command Center", icon: LayoutDashboard, end: true },
  { to: "/platform", label: "Platform", icon: LayoutGrid },
  { to: "/factory", label: "Idea Factory", icon: Factory },
  { to: "/matrix", label: "Matrix", icon: Atom },
  { to: "/sources", label: "10 Sources", icon: Compass },
  { to: "/discover", label: "Innovation Engine", icon: Atom },
  { to: "/projects", label: "Innovation Pipeline", icon: FolderKanban },
  { to: "/demo", label: "Demo Mode", icon: Sparkles },
  { to: "/library", label: "Problem Library", icon: BookOpen },
  { to: "/pricing", label: "Pricing & Plans", icon: BadgeDollarSign },
];

const VENTURE_TABS = [
  { tab: "overview", label: "Overview", icon: LayoutDashboard },
  { tab: "innovation", label: "Innovation", icon: Lightbulb },
  { tab: "prototype", label: "Prototype", icon: Wrench },
  { tab: "validation", label: "Validation", icon: Activity },
  { tab: "regulatory", label: "Regulatory", icon: ShieldCheck },
  { tab: "clinical", label: "Clinical", icon: FlaskConical },
  { tab: "commercial", label: "Commercial", icon: Building2 },
  { tab: "founder-review", label: "Founder Review", icon: AlertOctagon },
  { tab: "investor", label: "Investor Mode", icon: TrendingUp },
  { tab: "scorecard", label: "Scorecard", icon: ListTree },
  { tab: "report", label: "Final Report", icon: ScrollText },
  { tab: "pitch", label: "Pitch Deck", icon: FileText },
  { tab: "workflow", label: "AI Workflow", icon: Network },
  { tab: "trace", label: "Trace", icon: ScrollText },
];

function LeftSidebar({ collapsed, onToggle, ventureId }) {
  const { setCmdOpen } = useLayout();
  return (
    <aside data-testid="left-sidebar" className="h-full flex flex-col bg-white/[0.015]">
      {/* Logo */}
      <div className={`shrink-0 ${collapsed ? "px-2 py-5" : "px-4 py-5"} flex items-center justify-between border-b border-white/[0.06]`}>
        <Link to="/" className="flex items-center gap-3 min-w-0" data-testid="logo-link">
          <div className="relative shrink-0">
            <div className="absolute inset-0 -m-3 rounded-full blur-2xl bg-white/[0.06] pointer-events-none" aria-hidden="true" />
            <img
              src="/aignoz-logo.png"
              alt="Aignoz"
              className="relative h-11 w-11 object-contain"
            />
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <div className="font-semibold text-sm tracking-tight truncate">Aignoz</div>
              <div className="text-[9px] uppercase tracking-[0.18em] text-white/40 truncate">Innovation OS · DeepTech Engine™</div>
            </div>
          )}
        </Link>
        {!collapsed && (
          <button onClick={onToggle} data-testid="sidebar-collapse" className="text-white/40 hover:text-white/80 transition">
            <PanelLeftClose className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Command bar */}
      {!collapsed && (
        <button data-testid="cmdk-trigger" onClick={() => setCmdOpen(true)}
          className="mx-3 mt-3 flex items-center gap-2 px-3 py-2 rounded-md border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] transition text-left">
          <Search className="w-3.5 h-3.5 text-white/40" />
          <span className="text-xs text-white/40 flex-1">Quick search</span>
          <kbd className="text-[9px] mono px-1.5 py-0.5 rounded bg-white/[0.06] text-white/60">⌘K</kbd>
        </button>
      )}

      {/* Persona pill */}
      {!collapsed && (
        <div className="mx-3 mt-2 flex items-center gap-2">
          <div className="text-[9px] uppercase tracking-[0.18em] text-white/30">Lens</div>
          <PersonaPill />
        </div>
      )}

      {/* Sections */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin px-2 py-3 space-y-4">
        <Section title={collapsed ? "" : "Workspace"} collapsed={collapsed}>
          {WORKSPACE_LINKS.map((l) => <NavRow key={l.to} {...l} collapsed={collapsed} />)}
        </Section>

        {ventureId && (
          <Section title={collapsed ? "" : "Current Venture"} collapsed={collapsed} accent>
            {VENTURE_TABS.map((t) => (
              <NavRow key={t.tab} to={`/projects/${ventureId}/${t.tab}`} label={t.label} icon={t.icon} collapsed={collapsed} testIdSuffix={t.tab} />
            ))}
          </Section>
        )}
      </nav>

      {/* Footer */}
      <div className={`shrink-0 border-t border-white/[0.06] ${collapsed ? "px-2 py-3" : "px-3 py-3"} flex items-center gap-2`}>
        {collapsed ? (
          <button onClick={onToggle} className="w-full h-8 rounded-md hover:bg-white/[0.04] flex items-center justify-center text-white/40" data-testid="sidebar-expand">
            <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <>
            <Link to="/agents" className="text-[10px] text-white/30 hover:text-white/60 transition" data-testid="footer-dev">Developer</Link>
            <span className="text-[10px] text-white/20">·</span>
            <span className="text-[10px] text-white/30">GPT-5</span>
          </>
        )}
      </div>
    </aside>
  );
}

function Section({ title, accent = false, collapsed, children }) {
  return (
    <div>
      {title && (
        <div className={`px-2 mb-1.5 text-[9px] uppercase tracking-[0.18em] ${accent ? "text-teal-300/70" : "text-white/30"}`}>{title}</div>
      )}
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function NavRow({ to, label, icon: Icon, end, collapsed, testIdSuffix }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={`nav-${testIdSuffix || to.replace(/[^a-z0-9]/gi, "-")}`}
      className={({ isActive }) =>
        `relative flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[12.5px] transition-all duration-200 group ${
          isActive
            ? "bg-teal-400/10 text-teal-200 border border-teal-400/20"
            : "text-white/55 hover:text-white hover:bg-white/[0.03] border border-transparent"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={`absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-r-sm transition-all duration-200 ${
              isActive
                ? "bg-[#D17EC0] opacity-100"
                : "bg-[#D17EC0] opacity-0 group-hover:opacity-70"
            }`}
            aria-hidden="true"
          />
          <Icon className="w-3.5 h-3.5 shrink-0" />
          {!collapsed && <span className="truncate">{label}</span>}
        </>
      )}
    </NavLink>
  );
}

// ---------- Right Insights Panel ----------
function RightInsights({ collapsed, onToggle }) {
  const { insights } = useLayout();
  const location = useLocation();
  const m = location.pathname.match(/^\/projects\/([^/]+)/);
  const ventureId = m && m[1] !== "new" ? m[1] : null;
  const tab = location.pathname.split("/").pop();

  if (collapsed) {
    return (
      <div className="h-full flex flex-col items-center py-3 bg-white/[0.015]" data-testid="right-collapsed">
        <button onClick={onToggle} className="text-white/40 hover:text-white/80 transition" data-testid="right-expand">
          <ChevronLeft className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <aside data-testid="right-panel" className="h-full flex flex-col bg-white/[0.015]">
      <div className="shrink-0 px-4 py-3 border-b border-white/[0.06] flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-[0.18em] text-white/40">Insights</div>
          {ventureId ? (
            <div className="text-xs truncate text-white/80 mono mt-0.5">{ventureId.slice(0, 8)} · <span className="text-teal-300/80 capitalize">{tab}</span></div>
          ) : (
            <div className="text-xs truncate text-white/60 mt-0.5">Context-sensitive</div>
          )}
        </div>
        <button onClick={onToggle} data-testid="right-collapse" className="text-white/40 hover:text-white/80 transition">
          <PanelRightClose className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-thin p-4">
        {insights || <DefaultInsights />}
      </div>
    </aside>
  );
}

function DefaultInsights() {
  return (
    <div className="space-y-6" data-testid="default-insights">
      {/* Branded empty-state */}
      <div className="flex flex-col items-center justify-center text-center pt-6 pb-4 px-2">
        <div className="relative mb-5 opacity-90">
          <div className="absolute inset-0 -m-4 rounded-full blur-2xl bg-[#D17EC0]/15 pointer-events-none" />
          <img src="/aignoz-logo.png" alt="" className="relative h-16 w-16 object-contain" />
        </div>
        <h3 className="text-base font-semibold text-white/90 mb-2">Your Copilot waits here</h3>
        <p className="text-[12px] text-white/55 mb-5 max-w-[240px] leading-relaxed">
          Generate or open a venture to see your Founder Copilot — TRL, risks, funding readiness, and next actions.
        </p>
        <div className="flex flex-col gap-1.5 w-full">
          <Link to="/projects/new" data-testid="empty-cta-generate"
            className="text-xs px-3 py-2 rounded-md bg-teal-400 hover:bg-teal-300 text-slate-950 font-semibold inline-flex items-center justify-center gap-1.5 transition">
            Generate Venture <ArrowRight className="w-3 h-3" />
          </Link>
          <Link to="/demo" data-testid="empty-cta-demo"
            className="text-xs px-3 py-2 rounded-md border border-white/[0.08] text-white/75 hover:text-white hover:border-[#D17EC0]/40 hover:bg-white/[0.03] inline-flex items-center justify-center gap-1.5 transition">
            Run Demo Venture <ArrowRight className="w-3 h-3" />
          </Link>
          <Link to={`/projects/${FEATURED}/overview`} data-testid="empty-cta-featured"
            className="text-xs px-3 py-2 rounded-md border border-white/[0.08] text-white/75 hover:text-white hover:border-[#D17EC0]/40 hover:bg-white/[0.03] inline-flex items-center justify-center gap-1.5 transition">
            View Featured <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>

      {/* Existing Quick Actions retained below the brand card */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-2">Quick actions</div>
        <div className="space-y-1.5">
          <QuickAction to="/projects/new" icon={Rocket} label="Generate venture" />
          <QuickAction to={`/projects/${FEATURED}/overview`} icon={ArrowRight} label="Featured venture" />
          <QuickAction to={`/projects/${FEATURED}/pitch`} icon={FileText} label="Open pitch deck" />
        </div>
      </div>
      <div className="rounded-md border border-white/[0.06] bg-white/[0.015] p-3">
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-1">Tip</div>
        <div className="text-xs text-white/70 leading-relaxed">
          Press <kbd className="px-1 py-0.5 rounded bg-white/[0.06] text-[10px] mono">⌘K</kbd> anywhere to open the command palette.
        </div>
      </div>
    </div>
  );
}

function QuickAction({ to, icon: Icon, label }) {
  return (
    <Link to={to} className="flex items-center gap-2 px-2.5 py-2 rounded-md hover:bg-white/[0.03] text-xs text-white/70 hover:text-white transition group">
      <Icon className="w-3.5 h-3.5 text-teal-300/80" />
      <span className="flex-1">{label}</span>
      <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition" />
    </Link>
  );
}

// ---------- Command Palette (cmdk) ----------
function CommandPalette() {
  const { cmdOpen, setCmdOpen } = useLayout();
  const navigate = useNavigate();
  const { runs } = useRunsList();

  const go = (path) => { setCmdOpen(false); navigate(path); };
  const runDemo = async (s) => {
    setCmdOpen(false);
    try {
      const { run_id } = await apiPost("/innovate", s.inputs);
      toast.success(`Generating: ${s.title}`);
      navigate(`/projects/${run_id}/overview`);
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Dialog open={cmdOpen} onOpenChange={setCmdOpen}>
      <DialogContent className="p-0 max-w-xl glass-strong border-white/[0.08]" data-testid="cmd-palette">
        <Command label="Command Menu">
          <div className="flex items-center gap-2 px-3 border-b border-white/[0.06]">
            <CmdIcon className="w-3.5 h-3.5 text-white/40" />
            <Command.Input placeholder="Search ventures, jump to a tab, run a demo…" data-testid="cmd-input" />
            <kbd className="text-[9px] mono px-1.5 py-0.5 rounded bg-white/[0.06] text-white/60">ESC</kbd>
          </div>
          <Command.List>
            <Command.Empty>No matches.</Command.Empty>

            <Command.Group heading="Jump to">
              <Command.Item onSelect={() => go("/")}><LayoutDashboard className="w-3.5 h-3.5" />Command Center</Command.Item>
              <Command.Item onSelect={() => go(`/projects/${FEATURED}/overview`)}><Sparkles className="w-3.5 h-3.5" />Open Featured Venture</Command.Item>
              <Command.Item onSelect={() => go(`/projects/${FEATURED}/pitch`)}><FileText className="w-3.5 h-3.5" />Open Pitch Deck (Featured)</Command.Item>
              <Command.Item onSelect={() => go(`/projects/${FEATURED}/founder-review`)}><AlertOctagon className="w-3.5 h-3.5" />Founder Review (Featured)</Command.Item>
              <Command.Item onSelect={() => go("/projects/new")}><Rocket className="w-3.5 h-3.5" />New venture</Command.Item>
              <Command.Item onSelect={() => go("/projects")}><FolderKanban className="w-3.5 h-3.5" />Innovation Pipeline</Command.Item>
            </Command.Group>

            <Command.Group heading="Run a demo">
              {DEMO_SCENARIOS.map((s) => (
                <Command.Item key={s.id} onSelect={() => runDemo(s)} value={`demo ${s.title}`}>
                  <Sparkles className="w-3.5 h-3.5 text-teal-300/80" />{s.title}
                </Command.Item>
              ))}
            </Command.Group>

            {runs.length > 0 && (
              <Command.Group heading="Recent ventures">
                {runs.slice(0, 8).map((r) => (
                  <Command.Item key={r.run_id} onSelect={() => go(`/projects/${r.run_id}/overview`)} value={`venture ${r.company_name || r.problem} ${r.run_id}`}>
                    <FolderKanban className="w-3.5 h-3.5" />
                    <span className="truncate flex-1">{r.company_name || r.problem}</span>
                    <span className="text-[10px] text-white/30 mono">{r.run_id.slice(0, 6)}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}
          </Command.List>
        </Command>
      </DialogContent>
    </Dialog>
  );
}


// ---------- Phase 4 breadcrumb sub-nav (visible on Aignoz Idea Factory pages) ----------
function BreadcrumbVisibility() {
  const loc = useLocation();
  const p = loc.pathname.replace(/\/$/, "") || "/";
  const ON = new Set(["/", "/matrix", "/sources", "/factory", "/workspace", "/pricing", "/platform", "/captable", "/templates"]);
  if (!ON.has(p)) return null;
  return <BreadcrumbNav />;
}
